import { useEffect, useState } from "react";
import axios from "axios";
import { Calendar, Clock, MapPin, Repeat, AlertCircle } from "lucide-react";
import PageHeroBackground from "@/components/PageHeroBackground";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  Service: "bg-[#123F33]/10 text-[#123F33]",
  Revival: "bg-amber-100 text-amber-800",
  Outreach: "bg-blue-100 text-blue-800",
  Special: "bg-rose-100 text-rose-800",
};

function formatDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function dayParts(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return {
      day: d.getDate(),
      month: d.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
      weekday: d.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase(),
    };
  } catch {
    return { day: "—", month: "", weekday: "" };
  }
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/events`);
        if (!cancelled) setEvents(res.data || []);
      } catch (e) {
        if (!cancelled) setError("Unable to load events. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="events-page">
      {/* HERO */}
      <section className="relative pt-40 pb-16 bg-[#FAFAF7] overflow-hidden">
        <PageHeroBackground opacity={0.22} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.28em] text-[#123F33] mb-6 font-semibold">Events & Services</div>
            <h1 className="font-serif text-5xl sm:text-6xl text-[#0B1D2E] leading-[1.05] tracking-tight">
              What's happening at
              <span className="block italic text-[#123F33]">The Potter's House.</span>
            </h1>
            <p className="mt-8 text-lg text-[#4A5562] leading-relaxed">
              Weekly services, revival nights, outreach, and special gatherings — all under one roof in DeLand, Florida.
            </p>
          </div>
        </div>
      </section>

      {/* EVENTS LIST */}
      <section className="bg-white pb-28 pt-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          {loading && (
            <div className="py-20 text-center text-[#4A5562]" data-testid="events-loading">
              Loading events…
            </div>
          )}

          {error && (
            <div className="py-12 flex items-center gap-3 text-rose-700 bg-rose-50 rounded-xl px-6" data-testid="events-error">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && events.length === 0 && (
            <div className="py-20 text-center text-[#4A5562]" data-testid="events-empty">
              No upcoming events yet. Check back soon.
            </div>
          )}

          {!loading && !error && events.length > 0 && (
            <ul className="divide-y divide-black/10 border-t border-black/10" data-testid="events-list">
              {events.map((ev) => {
                const parts = dayParts(ev.date);
                return (
                  <li
                    key={ev.id}
                    data-testid={`event-${ev.id}`}
                    className="group py-8 md:py-10 grid grid-cols-12 gap-6 md:gap-10 items-center hover:bg-[#FAFAF7] transition-colors px-2 md:px-4 rounded-none"
                  >
                    <div className="col-span-3 md:col-span-2 flex flex-col items-start">
                      <div className="text-xs tracking-[0.22em] uppercase text-[#4A5562]">{parts.weekday}</div>
                      <div className="font-serif text-5xl md:text-6xl text-[#0B1D2E] leading-none mt-1">{parts.day}</div>
                      <div className="text-xs tracking-[0.22em] uppercase text-[#123F33] mt-1">{parts.month}</div>
                    </div>

                    <div className="col-span-9 md:col-span-7">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 rounded-full ${CATEGORY_COLORS[ev.category] || "bg-[#F3F2EB] text-[#0B1D2E]"}`}>
                          {ev.category}
                        </span>
                        {ev.recurring && (
                          <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 rounded-full bg-[#F3F2EB] text-[#4A5562]">
                            <Repeat className="w-3 h-3" /> Weekly
                          </span>
                        )}
                      </div>
                      <h3 className="font-serif text-2xl md:text-3xl text-[#0B1D2E] leading-tight group-hover:text-[#123F33] transition-colors">
                        {ev.title}
                      </h3>
                      {ev.description && (
                        <p className="mt-2 text-[#4A5562] leading-relaxed max-w-2xl">{ev.description}</p>
                      )}
                    </div>

                    <div className="hidden md:flex col-span-3 flex-col items-end text-sm text-[#4A5562] gap-2">
                      <div className="inline-flex items-center gap-2"><Clock className="w-4 h-4" /> {ev.time}</div>
                      <div className="inline-flex items-center gap-2 text-right"><MapPin className="w-4 h-4 shrink-0" /> <span>{ev.location}</span></div>
                    </div>

                    <div className="md:hidden col-span-12 flex flex-wrap gap-4 text-sm text-[#4A5562]">
                      <div className="inline-flex items-center gap-2"><Clock className="w-4 h-4" /> {ev.time}</div>
                      <div className="inline-flex items-center gap-2"><MapPin className="w-4 h-4" /> {ev.location}</div>
                      <div className="inline-flex items-center gap-2"><Calendar className="w-4 h-4" /> {formatDate(ev.date)}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
